// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Badge, Drawer, IconButton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { classList } from 'services/styles'
import { MoreHoriz } from '@mui/icons-material'
import { makeStyles } from 'tss-react/mui'
import { useForceUpdate } from 'hooks/utility'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'

const useStyles = makeStyles()(theme => ({
  wrapper: {
    position: 'relative' as 'relative',
    zIndex: '5',
    minHeight: '100%',
    marginRight: theme.spacing(4),
    marginLeft: theme.spacing(4),
    marginBottom: theme.spacing(4),
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  container: {
    width: 320,
    maxWidth: '100vw',
    boxSizing: 'border-box' as 'border-box',
    display: 'flex',
    justifyContent: 'flex-end',
    minHeight: '100%',
    position: 'relative' as 'relative',
  },
  content: {
    width: '100%',
  },
  sticky: {
    '& > *': {
      position: 'sticky' as 'sticky',
      top: 24,
    },
  },
}))

export interface IPageSidebarProps {
  sticky?: boolean
  mobile?: 'button' | 'hidden'
  mobileIcon?: React.ReactNode
  hidden?: boolean | number
  children?: any
  classes?: Partial<Record<string, string>>
}

export interface IPageSidebarState {
  isDrawerOpen: boolean
}

function PageSidebar(props: IPageSidebarProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)

  const forceUpdate = useForceUpdate()

  useEffect(() => {
    forceUpdate()
  }, [])

  const { classes } = useStyles()
  const theme = useTheme()
  const { children, sticky, mobile, mobileIcon = <MoreHoriz />, hidden, ...restProps } = props

  let hiddenThreshold = theme.breakpoints.values.lg

  if (typeof hidden === 'number') {
    hiddenThreshold = hidden
  } else if (typeof hidden === 'boolean') {
    hiddenThreshold = hidden ? 100000 : 0
  }

  const isMatch = useResponsiveBreakpoint(hiddenThreshold)

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  const renderChildren = (c) => {
    if (typeof c === 'function') {
      return c({
        isDrawerOpen: isDrawerOpen,
        closeDrawer: handleDrawerClose,
      })
    }

    return c
  }

  const renderMobileMode = () => {
    const target = document.getElementById('mobile-sidebar-button-container')

    if (!target) {
      return null
    }

    switch (mobile) {
      case 'button':
        return ReactDOM.createPortal(
          (
            <React.Fragment>
              <Badge>
                <IconButton
                  onClick={handleDrawerOpen}
                  data-cy="mobile-navigation"
                  size="large"
                >
                  {mobileIcon}
                </IconButton>
              </Badge>

              <Drawer open={isDrawerOpen} onClose={handleDrawerClose} anchor="right">
                {renderChildren(children)}
              </Drawer>
            </React.Fragment>
          ),
          target,
        )

      default:
        // assume 'hidden'
        return null
    }
  }

  return (
    <React.Fragment>
      {isMatch && renderMobileMode()}

      {!isMatch && (
        <div className={classes.wrapper}>
          <div className={classes.container} {...restProps}>
            <div className={classList(classes.content, sticky && classes.sticky)}>
              <div>
                {renderChildren(children)}
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  )
}

export default PageSidebar
